import { supabase } from '@/integrations/supabase/client';
import type { ContentSuggestion } from '@/types/suggestions';

// Structured content types for rich feedback rendering
export interface StructuredReplacement {
  original: string;
  replacement: string;
  reason?: string;
}

export interface StructuredNumberedItem {
  heading: string;
  content: string;
}

export interface StructuredSection {
  title: string;
  type: 'list' | 'numbered' | 'replacements' | 'text';
  items: (string | StructuredNumberedItem | StructuredReplacement)[];
  content?: string; // For 'text' type
}

export interface StructuredContent {
  type: 'feedback' | 'analysis' | 'ideas';
  summary: string;
  sections: StructuredSection[];
  questions?: string[];
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  openai_response_id?: string;
  parent_response_id?: string;
  has_draft: boolean;
  draft_content?: string;
  draft_content_id?: string;
  structured_content?: StructuredContent;
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  credits_consumed?: number;
  used_web_search: boolean;
  web_search_results?: any;
  created_at: string;
}

export interface ChatConversation {
  id: string;
  user_id: string;
  title?: string;
  writing_style_id?: string;
  brand_profile_id?: string;
  purpose?: string;
  channel?: string;
  target_audience?: string;
  seo_keywords?: string[];
  story_framework?: string;
  status: 'active' | 'archived' | 'deleted';
  created_at: string;
  updated_at: string;
}

export interface ChatContext {
  writing_style_id?: string;
  brand_profile_id?: string;
  purpose?: string;
  channel?: string;
  target_audience?: string;
  seo_keywords?: string[];
  /** Story framework for content structure (e.g., PAS, AIDA, BAB) */
  story_framework?: string;
  /** Current document content for context-aware suggestions (Write page only) */
  current_content?: string;
  /** Selected text from editor - when present, AI focuses on this portion */
  selected_text?: string;
  /** Content piece ID if editing existing content */
  content_id?: string;
}

export interface ChatCredits {
  consumed: number;
  source: string;
  overage: number;
  tokens: {
    input: number;
    output: number;
    total: number;
  };
  remaining: {
    free: number;
  };
}

export interface ChatResponse {
  message_id: string;
  conversation_id: string;
  response_id: string;
  content: string;
  has_draft: boolean;
  draft_content?: string;
  /** ID of the auto-created content piece when a draft is generated */
  content_piece_id?: string;
  /** True if an existing content piece was updated (vs new one created) */
  content_updated?: boolean;
  structured_content?: StructuredContent;
  /** Content suggestions for inline diffs (when editing existing content) */
  suggestions?: ContentSuggestion[];
  /** Compaction data for frontend to trigger async compaction */
  _compaction_data?: {
    full_conversation: unknown[];
    system_prompt: string;
  };
  used_web_search: boolean;
  citations: { title: string; url: string }[];
  credits: ChatCredits;
}

export interface SendMessageParams {
  message: string;
  conversation_id?: string;
  previous_response_id?: string;
  // Note: compacted_context is managed server-side in database
  context?: ChatContext;
  /** Action type for smart routing (e.g., 'analyze' triggers full document analysis) */
  action_type?: 'analyze' | null;
}

/**
 * Send a message to the chat AI and get a response
 */
export async function sendChatMessage(params: SendMessageParams): Promise<ChatResponse> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const response = await supabase.functions.invoke('chat-generate', {
    body: {
      message: params.message,
      conversation_id: params.conversation_id,
      previous_response_id: params.previous_response_id,
      // compacted_context is managed server-side in database
      writing_style_id: params.context?.writing_style_id,
      brand_profile_id: params.context?.brand_profile_id,
      purpose: params.context?.purpose,
      channel: params.context?.channel,
      target_audience: params.context?.target_audience,
      seo_keywords: params.context?.seo_keywords,
      story_framework: params.context?.story_framework,
      // Content-aware fields for Write page integration
      current_content: params.context?.current_content,
      selected_text: params.context?.selected_text,
      content_id: params.context?.content_id,
      // Smart routing action type
      action_type: params.action_type,
    },
  });

  if (response.error) {
    throw new Error(response.error.message || 'Failed to send message');
  }

  return response.data as ChatResponse;
}

/**
 * Get all conversations for the current user
 */
export async function getConversations(): Promise<ChatConversation[]> {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('*')
    .eq('status', 'active')
    .order('updated_at', { ascending: false });

  if (error) {
    throw new Error(error.message);
  }

  return data as ChatConversation[];
}

/**
 * Get a single conversation by ID
 */
export async function getConversation(id: string): Promise<ChatConversation | null> {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw new Error(error.message);
  }

  return data as ChatConversation;
}

/**
 * Get all messages for a conversation
 */
export async function getConversationMessages(conversationId: string): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(error.message);
  }

  return data as ChatMessage[];
}

/**
 * Update conversation context settings
 */
export async function updateConversationContext(
  conversationId: string,
  context: ChatContext
): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations')
    .update({
      writing_style_id: context.writing_style_id,
      brand_profile_id: context.brand_profile_id,
      purpose: context.purpose,
      channel: context.channel,
      target_audience: context.target_audience,
      seo_keywords: context.seo_keywords,
      story_framework: context.story_framework,
    })
    .eq('id', conversationId);

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Update conversation title
 */
export async function updateConversationTitle(
  conversationId: string,
  title: string
): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations')
    .update({ title })
    .eq('id', conversationId);

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Archive a conversation
 */
export async function archiveConversation(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations')
    .update({ status: 'archived' })
    .eq('id', conversationId);

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_conversations')
    .update({ status: 'deleted' })
    .eq('id', conversationId);

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Save a draft from chat to content pieces
 */
export async function saveDraftToContent(
  conversationId: string,
  messageId: string,
  draftContent: string,
  title?: string,
  context?: ChatContext
): Promise<string> {
  // Get current user
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  if (userError || !user) {
    throw new Error('Not authenticated');
  }

  // Create content piece with context metadata
  const { data: contentPiece, error: contentError } = await supabase
    .from('content_pieces')
    .insert({
      user_id: user.id,
      title: title || 'Draft from Chat',
      content: draftContent,
      medium: 'custom',
      custom_medium: context?.channel || null,
      purpose_description: context?.purpose || null,
      target_audience: context?.target_audience || null,
      target_keywords: context?.seo_keywords || null,
      brand_profile_id: context?.brand_profile_id || null,
      writing_style_id: context?.writing_style_id || null,
      story_framework: context?.story_framework || null,
      conversation_id: conversationId,
      status: 'draft',
      tags: ['chat-generated'],
    })
    .select('id')
    .single();

  if (contentError) {
    throw new Error(contentError.message);
  }

  // Link content piece to message
  const { error: updateError } = await supabase
    .from('chat_messages')
    .update({ draft_content_id: contentPiece.id })
    .eq('id', messageId);

  if (updateError) {
    console.error('Failed to link content piece to message:', updateError);
  }

  return contentPiece.id;
}

/**
 * Compact a conversation's context for cost-efficient future turns
 * Called asynchronously after receiving a chat response
 */
export async function compactConversation(
  conversationId: string,
  fullConversation: unknown[],
  systemPrompt?: string
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    console.warn('[compactConversation] Not authenticated, skipping compaction');
    return;
  }

  try {
    const response = await supabase.functions.invoke('compact-conversation', {
      body: {
        conversation_id: conversationId,
        full_conversation: fullConversation,
        system_prompt: systemPrompt,
      },
    });

    if (response.error) {
      console.error('[compactConversation] Error:', response.error);
    }
  } catch (err) {
    // Fire-and-forget - don't throw, just log
    console.error('[compactConversation] Failed:', err);
  }
}

/**
 * Get the last response ID for a conversation (for threading)
 */
export async function getLastResponseId(conversationId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('openai_response_id')
    .eq('conversation_id', conversationId)
    .eq('role', 'assistant')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No messages yet
    throw new Error(error.message);
  }

  return data?.openai_response_id || null;
}
